/**
 * CapacitySection — the "Capacity" panel at the top of the Rules tab.
 * Named capacity rules per SLOT — Default paths, Rule paths (per EACH rule
 * group), landers/offers (gated) — with a live active/max readout. Enforced
 * by the queue rotation (over-cap = worst formula scores parked in Waiting).
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC, useMemo } from 'react';
import { Gauge, Plus } from 'lucide-react';
import type { CapacityRule } from '@trafficpulse/shared';

import { useOptionalFlowOptimizerLivePreview } from '@/features/flow-optimizer/context/FlowOptimizerLivePreviewContext';

import CapacityRuleCard from './CapacityRuleCard';
import type { RuleGroupMeter } from './meters';
import {
  activeCountBySlot,
  firstFreeSlot,
  makeCapacityRule,
  removeRule,
  slotsUsedByOthers,
  updateRule,
} from './capacity-rules';

export interface CapacitySectionProps {
  rules: CapacityRule[];
  onChange: (next: CapacityRule[]) => void;
}

const CapacitySection: FC<CapacitySectionProps> = ({ rules, onChange }) => {
  const preview = useOptionalFlowOptimizerLivePreview();
  const counts = useMemo(
    () => activeCountBySlot(preview?.result?.items ?? []),
    [preview?.result],
  );
  // Per-rule-group meters: pool names from the same preview fetch.
  const ruleGroups = useMemo<RuleGroupMeter[]>(() => {
    const pools = preview?.result?.pools ?? [];
    return [...counts.byRuleGroup.entries()].map(([id, active]) => ({
      id,
      name: pools.find((p) => p.id === id)?.name ?? id,
      active,
    }));
  }, [counts, preview?.result]);
  const freeSlot = firstFreeSlot(rules);

  return (
    <section className="space-y-2 rounded-lg border border-border/70 bg-muted/10 p-3">
      <div className="flex items-center gap-2">
        <Gauge className="h-4 w-4 text-muted-foreground" aria-hidden />
        <h3 className="text-[13px] font-semibold">Capacity</h3>
        <span className="text-[11px] text-muted-foreground">
          How many can run live at once
        </span>
      </div>

      {rules.length > 0 ? (
        <div className="space-y-1.5">
          {rules.map((rule) => (
            <CapacityRuleCard
              key={rule.id}
              rule={rule}
              counts={counts}
              ruleGroups={ruleGroups}
              disabledSlots={slotsUsedByOthers(rules, rule.id)}
              onChange={(next) => onChange(updateRule(rules, next))}
              onRemove={() => onChange(removeRule(rules, rule.id))}
            />
          ))}
        </div>
      ) : (
        <p className="text-[12px] text-muted-foreground">
          No cap set — every path the queue promotes stays live. Add a rule to
          limit the default paths, or how many each rule group may hold.
        </p>
      )}

      <button
        type="button"
        disabled={freeSlot === null}
        onClick={() => freeSlot && onChange([...rules, makeCapacityRule(freeSlot)])}
        title={
          freeSlot === null
            ? 'All available slots are capped — one rule per slot (landers/offers coming soon)'
            : 'Add a capacity rule'
        }
        className="inline-flex h-7 items-center gap-1 rounded-md border border-dashed border-border px-2.5 text-[11px] font-medium text-muted-foreground transition-colors hover:border-primary/50 hover:text-primary disabled:cursor-not-allowed disabled:opacity-50 disabled:hover:border-border disabled:hover:text-muted-foreground"
      >
        <Plus className="h-3 w-3" aria-hidden />
        Add capacity rule
      </button>
    </section>
  );
};

export default CapacitySection;
